import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { isError, isString } from 'lodash';
import { connect } from 'react-redux';
import { Map } from 'immutable';
import classNames from 'classnames';
import { injectIntl } from 'react-intl';
import { Alert, Modal } from 'react-bootstrap';
import { createCategorySuccess, deleteCategorySuccess, retrieveCategories } from '../../actions/categories';
import { hideCategoryMoveDialog } from '../../actions/mainUI';
import Button from '../ui/Button';
import Loader from '../ui/Loader';
import CategoryTree from '../CategoryTree';
import { moveCategory } from '../../utils/piction';
import { SESSION_EXPIRED_MESSAGE } from '../../constants/sessionStateTypes';
import { sessionExpired } from '../../actions/session';
import { addIntlInfo } from '../../actions/messageDialog';
import CloseIcon from '../Icons/CloseIcon';

const mapStateToProps = state => ({
  categoriesState: state.categories,
});

const mapDispatchToProps = dispatch => ({
  createCategorySuccess: (categoryID, categoryName, categoryParentID) =>
    dispatch(createCategorySuccess(categoryID, categoryName, categoryParentID, '')),
  deleteCategorySuccess: categoryID => dispatch(deleteCategorySuccess(categoryID)),
  hideCategoryMoveDialog: () =>
    dispatch(hideCategoryMoveDialog()),
  retrieveCategories: () => dispatch(retrieveCategories()),
  sessionExpired: err => dispatch(sessionExpired(err)),
  addMessage: msgID => dispatch(addIntlInfo(msgID)),
});

const defaultState = {
  isSubmitting: false,
  error: null,
  isSuccess: false,
  selectedCategoryID: null,
  selectedCollectionID: null,
  selectedCategoryTitle: null,
};

class CategoryMoveDialog extends Component {
  constructor(props) {
    super(props);

    this.state = {
      ...defaultState,
    };

    this.onHide = this.onHide.bind(this);
    this.onExit = this.onExit.bind(this);
    this.onTreeSelect = this.onTreeSelect.bind(this);
    this.onMoveClick = this.onMoveClick.bind(this);
  }

  onExit() {
    this.setState({ ...defaultState });
  }

  onHide(forceHide = false) {
    if (!this.state.isSubmitting || forceHide) {
      this.props.hideCategoryMoveDialog();
    }
  }

  onMoveClick() {
    this.setState({ isSubmitting: true, isSuccess: false, error: null });

    let destinationCategoryID = this.state.selectedCategoryID;
    let destinationCollectionID = this.state.selectedCollectionID;

    // if there is no collection id, it means that the destination is the root level of a
    // collection and the recorded category id is actually the collection id.
    if (!destinationCollectionID) {
      destinationCollectionID = destinationCategoryID;
      destinationCategoryID = '0';
    }

    moveCategory(
      this.props.categoryID,
      destinationCategoryID,
      destinationCollectionID,
      )
      .catch((error) => {
        if (error.message && error.message.includes(SESSION_EXPIRED_MESSAGE)) {
          this.props.sessionExpired(error);
        }
        return error;
      })
      .then((res) => {
        const isSuccess = !isError(res);
        this.setState({
          isSubmitting: false,
          isSuccess,
          error: isSuccess ? null : res,
        });

        if (isSuccess) {
          this.props.addMessage('categoryMoveDialog.successMoveMessage');
          this.props.retrieveCategories();
          this.onHide(true);
        }
      });
  }

  onTreeSelect(selectedKeys, e) {
    this.setState({
      error: null,
      isSuccess: false,
      selectedCategoryID: selectedKeys[0],
      selectedCollectionID: e.node.props.collectionID,
      selectedCategoryTitle: e.node.props.title,
    });
  }

  render() {
    const { intl } = this.props;
    let message;

    if (this.state.error) {
      const errMsg = this.state.error.message && isString(this.state.error.message)
        ? this.state.error.message
        : 'utils.piction.moveCategoryGenericError';
      message = (
        <Alert variant="danger" className="category-dialog__message">
          {intl.formatMessage({ id: errMsg })}
        </Alert>
      );
    } else if (this.state.isSuccess) {
      message = (
        <Alert variant="success" className="category-dialog__message">
          {intl.formatMessage({ id: 'categoryMoveDialog.successMoveMessage' })}
        </Alert>
      );
    }

    return (
      <div className="category-dialog">
        <Modal
          show={this.props.isShown}
          onHide={this.onHide}
          onExit={this.onExit}
          backdrop={this.state.isSubmitting ? 'static' : true}
          dialogClassName={classNames('category-dialog__overlay')}
        >
          <CloseIcon
            width={32}
            height={32}
            onClick={this.onHide}
            className="category-dialog__close-button"
          />

          <div className="category-dialog__form">
            <div className="category-dialog__form-header">
              {intl.formatMessage({ id: 'categoryMoveDialog.moveHeader' })}

              <div className="category-dialog__form-header-sub">
                {intl.formatMessage({ id: 'categoryMoveDialog.moveSubtext'}, { categoryName: this.props.categoryName })}
              </div>
            </div>

            <CategoryTree
              onSelect={this.onTreeSelect}
              isRetrievingRoot={this.props.categoriesState.get('isRetrievingRoot')}
              categoryList={this.props.categoriesState.get('categoryList')}
            />

            { message }
          </div>

          <Modal.Footer>
            <Button disabled={this.state.isSubmitting} onClick={this.onHide}>
              {intl.formatMessage({ id: 'categoryMoveDialog.cancelButton' })}
            </Button>

            <Button
              primary type="submit"
              disabled={!this.state.selectedCategoryID || this.state.isSubmitting}
              onClick={this.onMoveClick}
            >
              {this.state.isSubmitting ?
                <Loader /> :
                intl.formatMessage({ id: 'categoryMoveDialog.moveButton' })
              }
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}

CategoryMoveDialog.propTypes = {
  isShown: PropTypes.bool,
  categoryID: PropTypes.string,
  categoryName: PropTypes.string,
  hideCategoryMoveDialog: PropTypes.func,
  createCategorySuccess: PropTypes.func,
  deleteCategorySuccess: PropTypes.func,
  categoriesState: PropTypes.instanceOf(Map),
  sessionExpired: PropTypes.func,
  addMessage: PropTypes.func,
  retrieveCategories: PropTypes.func,
};

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(CategoryMoveDialog));
